import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    Input,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Field from '../../../../components/field'
import useContactUsFields from './use-contactUs-fields'
import {FormattedMessage, useIntl} from 'react-intl'
import AddressFieldsWithAutocomplete from '../../../../components/autocomplete-address-fields'
import {validateFile} from '../../../../utils/upload-utils'
import SanitizedHtml from '../../../../components/html-sanitizer'

const ContactUsFields = ({form, prefix = '', setReceiptBase64, setReceiptName, setReceiptMime}) => {
    const fields = Object.fromEntries(
        Object.entries(useContactUsFields({form, prefix})).filter(([key]) => key !== 'requestType'),
    )
    const formRef = useRef()
    const formStyles = useMultiStyleConfig('ContactUs')
    const [errorMessage, setErrorMessage] = useState('')
    const {formatMessage} = useIntl()

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        formRef?.current?.focus()
    }, [])

    const handleFileChange = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        const isValid = await validateFile(file)

        if (!isValid) {
            form.setValue?.(`${prefix}uploadFileName`, '')
            setErrorMessage(
                <FormattedMessage
                    id="contact_us.file_type_error"
                    defaultMessage="Only PDF/JPEG/PNG files are allowed"
                />,
            )
            return
        }

        if (file.size > 5 * 1024 * 1024) {
            form.setValue?.(`${prefix}uploadFileName`, '')
            setErrorMessage(
                <FormattedMessage
                    id="contact_us.file_size_error"
                    defaultMessage="File size exceeds 5 MB"
                />,
            )
            return
        }
        setErrorMessage('')
        // Set file properties
        setReceiptName(file.name)
        setReceiptMime(file.type)
        form.setValue?.(`${prefix}uploadFileName`, file.name)

        // Create a FileReader to convert file to base64
        const reader = new FileReader()
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1]
            setReceiptBase64(base64String)
        }
        reader.readAsDataURL(file)
    }

    // Define grouped fields for fields that should appear on the same row
    const groupedFields = [
        ['firstName', 'lastName'], // Group firstName and lastName together
        ['email', 'emailConfirmation'], // Group email and emailConfirmation together
        ['productName', 'purchaseDate'], // Group productName and purchaseDate together
        ['orderNumber', 'claimNumber'], // Group orderNumber and claimNumber together
        ['serialNumber', 'retailer'], // Group serialNumber and retailer together
    ]

    // Separate privacy and uploadFile fields for special handling
    const privacyField = fields.privacy
    const uploadFileField = fields.uploadFile

    // Render fields in the exact order defined in the preference configuration
    return (
        <Flex {...formStyles.formsContainer}>
            {/* Render description field */}
            {fields.description && (
                <Flex {...formStyles.formsPairContainer}>
                    <SanitizedHtml
                        as={Box}
                        html={fields.description.content}
                        {...formStyles.description}
                        sanitize={true}
                    />
                </Flex>
            )}
            {/* Render other fields */}
            {Object.keys(fields)
                .filter(
                    (fieldKey) =>
                        fieldKey !== 'description' &&
                        fieldKey !== 'privacy' &&
                        fieldKey !== 'uploadFile' &&
                        fieldKey !== 'city' &&
                        fieldKey !== 'stateCode' &&
                        fieldKey !== 'postalCode' &&
                        fieldKey !== 'countryCode' &&
                        fieldKey !== 'addressNumber',
                ) // Exclude description, privacy, uploadFile, and address fields from the loop
                .map((fieldKey, index) => {
                    // Check if the current field is part of a group
                    const group = groupedFields.find((group) => group.includes(fieldKey))

                    if (group) {
                        // Render the entire group if the current field is the first in the group
                        if (group[0] === fieldKey) {
                            // Filter out missing fields from the group
                            const validGroupFields = group.filter(
                                (groupFieldKey) => fields[groupFieldKey],
                            )

                            // If only one field exists, render it as a standalone field
                            if (validGroupFields.length === 1) {
                                const field = fields[validGroupFields[0]]
                                return (
                                    <Flex key={index} {...formStyles.formsPairContainer}>
                                        <Field {...field} />
                                    </Flex>
                                )
                            }

                            // Render the group if multiple fields exist
                            return (
                                <Flex key={index} {...formStyles.formsPairContainer}>
                                    {validGroupFields.map((groupFieldKey) => {
                                        const field = fields[groupFieldKey]
                                        if (field?.type === 'address') {
                                            // Render AddressFieldsWithAutocomplete for address type
                                            return (
                                                <AddressFieldsWithAutocomplete
                                                    key={groupFieldKey}
                                                    form={form}
                                                    variant="contactUs"
                                                    isContactUs
                                                    contactUsConfigFields={fields || {}}
                                                />
                                            )
                                        }
                                        return field ? (
                                            <Field key={groupFieldKey} {...field} />
                                        ) : null
                                    })}
                                </Flex>
                            )
                        }
                        // Skip rendering for subsequent fields in the group
                        return null
                    }

                    // Render standalone fields
                    const field = fields[fieldKey]
                    if (field?.type === 'address') {
                        // Render AddressFieldsWithAutocomplete for address type
                        return (
                            <Flex key={index} {...formStyles.formsPairContainer}>
                                <AddressFieldsWithAutocomplete
                                    form={form}
                                    variant="contactUs"
                                    isContactUs
                                    contactUsConfigFields={fields || {}}
                                />
                            </Flex>
                        )
                    }
                    return (
                        <Flex key={index} {...formStyles.formsPairContainer}>
                            <Field {...field} />
                        </Flex>
                    )
                })}
            {/* Render uploadFile field */}
            {uploadFileField && (
                <Flex {...formStyles.uploadFileContainer}>
                    <Button
                        {...formStyles.fileButton}
                        variant={'primary'}
                        onClick={() => document.getElementById(uploadFileField.name).click()}
                    >
                        {uploadFileField.label}
                    </Button>
                    <Field
                        name={`${prefix}uploadFileName`}
                        control={form.control}
                        defaultValue=""
                        label={formatMessage({
                            id: 'contact_us.upload_file',
                            defaultMessage: 'Upload file (Max 5 MB)',
                        })}
                        error={errorMessage ? {message: errorMessage} : undefined}
                        inputProps={{
                            readOnly: true,
                            pointerEvents: 'none',
                        }}
                    />
                    <Input
                        type="file"
                        id={uploadFileField.name}
                        {...uploadFileField}
                        onChange={handleFileChange}
                        style={{display: 'none'}}
                    />
                </Flex>
            )}
            {/* Render privacy field last */}
            {privacyField && (
                <Flex {...formStyles.formsPairContainer}>
                    <Field {...privacyField} />
                </Flex>
            )}
            <Flex {...formStyles.formsPairContainer}>
                <Text {...formStyles.personalData}>
                    <FormattedMessage
                        id="contact_us.personal_data"
                        defaultMessage="Processing of personal data pursuant to GDPR 679/2016{br}We wish to inform you that the personal data provided to us will be collected"
                        values={{
                            br: <br />,
                        }}
                    />
                </Text>
                <Box w="100%" display={{base: 'none', md: 'block'}} />
            </Flex>
        </Flex>
    )
}

ContactUsFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    setReceiptBase64: PropTypes.func.isRequired,
    setReceiptName: PropTypes.func.isRequired,
    setReceiptMime: PropTypes.func.isRequired,
}

export default ContactUsFields
